import type { WorkbookFreeformSheet } from "@erp/shared-types";
import { unescapeColumnReference } from "../formulaLexer";
import { resolveTable, type WorkbookTableItem } from "../tableReferences";
import { columnName, columnNumber, type CellRange } from "./address";

/** Which grid a freeform reference reads: this sheet's, or a table's by id or name. */
export type FreeformTable =
  | { kind: "local" }
  | { kind: "id"; id: string }
  | { kind: "name"; name: string };

/** Character offsets into the Formula, `end` exclusive. */
export interface FreeformSpan {
  start: number;
  end: number;
}

/**
 * One end of a reference's columns, as written: by position (`C`, A is 1),
 * or by name (`[Case Price]`), as a stored reference to another table is.
 */
export type FreeformColumn =
  { kind: "position"; column: number } | { kind: "name"; name: string };

/** One cell, range or column reference in a freeform Formula. */
export interface FreeformReference {
  table: FreeformTable;
  /**
   * Its first and last column as written, each with where it is in the
   * Formula: one for a single cell, two for a range or whole columns. Both
   * are by position or both by name.
   */
  columns: readonly { column: FreeformColumn; span: FreeformSpan }[];
  /** Its rows, first to last; `last` is null for whole columns (to the table's end). */
  rows: { first: number; last: number | null };
  /** Where its table qualifier (`'Prices'!`, `{workbook:ID}`) is in the Formula, when it has one. */
  qualifier: FreeformSpan | null;
  /** The whole reference, qualifier included. */
  span: FreeformSpan;
}

const IDENTIFIER = /[A-Za-z0-9_.]/;
const STORED = /^\{workbook:([0-9a-f-]{36})\}/;
const BARE = /^[A-Za-z_\\][A-Za-z0-9_.]*/;
const LETTERS = /^[A-Za-z]{1,3}/;
/** A column by name: `[Case Price]`, `]` escaped as `]]`, as a Column reference is. */
const NAMED = /^\[(?:[^\]]|\]\])+\]/;
const ROW = /^[1-9][0-9]*/;

/** A column end at `at`: `$`, then letters or (named) a bracketed name. */
function readColumn(
  text: string,
  at: number,
  named: boolean,
): { column: FreeformColumn; span: FreeformSpan; next: number } | null {
  const start = text[at] === "$" ? at + 1 : at;
  const match = (named ? NAMED : LETTERS).exec(text.slice(start));
  if (!match) return null;
  const end = start + match[0].length;
  const column: FreeformColumn = named
    ? { kind: "name", name: unescapeColumnReference(match[0]) }
    : { kind: "position", column: columnNumber(match[0]) };
  return { column, span: { start, end }, next: end };
}

/** A row at `at`: `$`, then its number. */
function readRow(
  text: string,
  at: number,
): { row: number; next: number } | null {
  const start = text[at] === "$" ? at + 1 : at;
  const match = ROW.exec(text.slice(start));
  return match
    ? { row: Number(match[0]), next: start + match[0].length }
    : null;
}

type ColumnEnd = NonNullable<ReturnType<typeof readColumn>>;
type Cells = Pick<FreeformReference, "columns" | "rows"> & { length: number };

/** Whole columns (`A:C`, `[SKU]:[Margin]`) at the start of `text`. */
function readWholeColumns(
  text: string,
  first: ColumnEnd,
  named: boolean,
): Cells | null {
  if (text[first.next] !== ":") return null;
  const last = readColumn(text, first.next + 1, named);
  // `A:B2` is no reference; neither is `A:B` inside a longer name.
  if (!last || IDENTIFIER.test(text[last.next] ?? "")) return null;
  return {
    columns: [first, last].map(({ column, span }) => ({ column, span })),
    rows: { first: 1, last: null },
    length: last.next,
  };
}

/** A cell or a range of cells at the start of `text`. */
function readCells(
  text: string,
  first: ColumnEnd,
  named: boolean,
): Cells | null {
  const firstRow = readRow(text, first.next);
  if (!firstRow) return null;
  const last = readLastCell(text, firstRow.next, named);
  const length = last?.next ?? firstRow.next;
  if (continuesName(text[length] ?? "")) return null;
  const ends = last ? [first, last.column] : [first];
  const rows = [firstRow.row, last?.row ?? firstRow.row];
  return {
    columns: ends.map(({ column, span }) => ({ column, span })),
    rows: { first: Math.min(...rows), last: Math.max(...rows) },
    length,
  };
}

/** A range's second cell after the first one's row (`:C9`), if there is one. */
function readLastCell(
  text: string,
  at: number,
  named: boolean,
): { column: ColumnEnd; row: number; next: number } | null {
  if (text[at] !== ":") return null;
  const column = readColumn(text, at + 1, named);
  const row = column && readRow(text, column.next);
  return column && row ? { column, row: row.row, next: row.next } : null;
}

/** Whether a character after a cell makes it no reference: a function name (LOG10), a longer name, or a sheet name. */
function continuesName(next: string): boolean {
  return next === "(" || next === "!" || IDENTIFIER.test(next);
}

/** The cells at the start of `text`, by position (`C2`) or by name (`[Case Price]2`). */
function readReference(text: string, named: boolean): Cells | null {
  const first = readColumn(text, 0, named);
  if (!first) return null;
  return readWholeColumns(text, first, named) ?? readCells(text, first, named);
}

/** A quoted sheet name at the start of `text` (`'Bob''s Prices'`): the name and length. */
function readQuoted(text: string): { name: string; length: number } | null {
  let name = "";
  for (let index = 1; index < text.length; index += 1) {
    const character = text[index];
    if (character !== "'") {
      name += character;
      continue;
    }
    if (text[index + 1] === "'") {
      name += "'";
      index += 1;
      continue;
    }
    return name ? { name, length: index + 1 } : null;
  }
  return null;
}

/** The table a qualifier at the start of `text` names, and its length before any `!`. */
function readTableName(
  text: string,
): { table: FreeformTable; length: number } | null {
  const stored = STORED.exec(text);
  if (stored?.[1])
    return { table: { kind: "id", id: stored[1] }, length: stored[0].length };
  const quoted = text.startsWith("'") ? readQuoted(text) : null;
  if (quoted)
    return {
      table: { kind: "name", name: quoted.name },
      length: quoted.length,
    };
  const bare = BARE.exec(text);
  return bare
    ? { table: { kind: "name", name: bare[0] }, length: bare[0].length }
    : null;
}

/**
 * The table qualifier at the start of `text`, if one is there: a table then
 * `!` before columns by position (`'Prices'!C2`, `{workbook:ID}!C2`), or a
 * table right before columns by name (`{workbook:ID}[Case Price]2`).
 */
function readQualifier(
  text: string,
): { table: FreeformTable; length: number; named: boolean } | null {
  const name = readTableName(text);
  if (!name) return null;
  const after = text.slice(name.length);
  if (after.startsWith("!"))
    return { table: name.table, length: name.length + 1, named: false };
  return /^\$?\[/.test(after)
    ? { table: name.table, length: name.length, named: true }
    : null;
}

/**
 * Every cell, range and column reference in a freeform cell's Formula, in
 * order: local ones (`A4`, `A1:A3`) and those to another table, as typed
 * (`'Prices'!B3`) or stored, by column name (`{workbook:ID}[Case Price]3`)
 * or, stored before names, by position (`{workbook:ID}!B3`). Text in quotes
 * is skipped. A literal (no leading `=`) has none.
 */
export function freeformReferences(input: string): FreeformReference[] {
  if (!input.startsWith("=")) return [];
  const references: FreeformReference[] = [];
  let index = 1;
  while (index < input.length) {
    const read = readAt(input, index);
    if (read.reference) references.push(read.reference);
    index = read.next;
  }
  return references;
}

/** What starts at `index`: a quoted text to skip, a reference, or one character. */
function readAt(
  input: string,
  index: number,
): { reference?: FreeformReference; next: number } {
  if (input[index] === '"') return { next: afterText(input, index) };
  // Inside a name (LOG10, Sheet1) or right after a qualifier is no reference.
  if (/[A-Za-z0-9_.'}]/.test(input[index - 1] ?? ""))
    return { next: index + 1 };
  return readReferenceAt(input, index);
}

/** Past the quoted text starting at `index`; `""` inside it is a quote, read by skipping to each closing quote. */
function afterText(input: string, index: number): number {
  const end = input.indexOf('"', index + 1);
  return end < 0 ? input.length : end + 1;
}

/** The reference starting at `index`, or the next place to look for one. */
function readReferenceAt(
  input: string,
  index: number,
): { reference?: FreeformReference; next: number } {
  const rest = input.slice(index);
  const qualifier = readQualifier(rest);
  const qualified = qualifier?.length ?? 0;
  const cells = readReference(rest.slice(qualified), qualifier?.named ?? false);
  if (!cells) return { next: index + Math.max(qualified, 1) };
  const at = index + qualified;
  return {
    reference: {
      table: qualifier?.table ?? { kind: "local" },
      columns: cells.columns.map(({ column, span }) => ({
        column,
        span: { start: at + span.start, end: at + span.end },
      })),
      rows: cells.rows,
      qualifier: qualifier ? { start: index, end: at } : null,
      span: { start: index, end: at + cells.length },
    },
    next: at + cells.length,
  };
}

/** Whether a reference names its columns (stored to another table since names). */
export function isNamedReference(reference: FreeformReference): boolean {
  return reference.columns[0]?.column.kind === "name";
}

/**
 * The rectangle a reference covers in its table's grid. A column by name is
 * at its place in `columns`, the table's columns in their current order, so
 * a range covers every column between its two ends' current places, as in
 * Excel. Not ok when a name isn't one of `columns` (`missing`), or when the
 * table's columns aren't known (`columns` undefined, `missing` null).
 */
export function freeformReferenceRange(
  reference: FreeformReference,
  columns?: readonly string[],
): { ok: true; range: CellRange } | { ok: false; missing: string | null } {
  const places: number[] = [];
  for (const { column } of reference.columns) {
    if (column.kind === "position") {
      places.push(column.column);
      continue;
    }
    if (!columns) return { ok: false, missing: null };
    const place = columns.indexOf(column.name) + 1;
    if (!place) return { ok: false, missing: column.name };
    places.push(place);
  }
  const { first, last } = reference.rows;
  return {
    ok: true,
    range: {
      start: { column: Math.min(...places), row: first },
      end: { column: Math.max(...places), row: last },
    },
  };
}

/** A table's name as a Formula writes it: bare when Excel allows, else quoted. */
export function freeformTableName(name: string): string {
  return /^[A-Za-z_][A-Za-z0-9_.]*$/.test(name) && !readReference(name, false)
    ? name
    : `'${name.replaceAll("'", "''")}'`;
}

/** A column as a stored reference names it: `[Case Price]`, `]` escaped as `]]`. */
export function freeformColumnName(name: string): string {
  return `[${name.replaceAll("]", "]]")}]`;
}

interface Edit {
  span: FreeformSpan;
  text: string;
}

/** Rewrites each reference's parts by span, leaving every other character as typed. */
function rewriteReferences(
  input: string,
  rewrite: (reference: FreeformReference) => Edit[],
): string {
  let result = "";
  let offset = 0;
  for (const reference of freeformReferences(input))
    for (const { span, text } of rewrite(reference)) {
      result += input.slice(offset, span.start) + text;
      offset = span.end;
    }
  return result + input.slice(offset);
}

/** A reference typed by position to another table, stored by its columns' names when it can be. */
function storedEdits(
  reference: FreeformReference,
  qualifier: FreeformSpan,
  item: WorkbookTableItem,
): Edit[] {
  const id = `{workbook:${item.id}}`;
  if (isNamedReference(reference)) return [{ span: qualifier, text: id }];
  const { columns } = item;
  const names = reference.columns.map(({ column }) =>
    column.kind === "position" ? columns?.[column.column - 1] : undefined,
  );
  // Past its last column, or its columns unknown: by position, as before names.
  if (!names.every((name) => name !== undefined))
    return [{ span: qualifier, text: `${id}!` }];
  return [
    { span: qualifier, text: id },
    ...reference.columns.map(({ span }, index) => ({
      span,
      text: freeformColumnName(names[index] ?? ""),
    })),
  ];
}

/**
 * The stored form of what was typed into a freeform cell: each table named
 * by one Source or Worksheet is stored by its id, and its columns by name
 * (`'Prices'!C2` → `{workbook:ID}[Case Price]2`) when the table's columns are
 * known (`WorkbookTableItem.columns`), else by position (`{workbook:ID}!C2`).
 * This sheet's own name is dropped (a local reference). A name no table has
 * is kept, so the Plan reports it.
 */
export function storedFreeformFormula(
  input: string,
  tables: readonly WorkbookTableItem[],
  selfId: string,
): string {
  return rewriteReferences(input, (reference) => {
    const { table, qualifier } = reference;
    if (!qualifier || table.kind !== "name") return [];
    const resolved = resolveTable(tables, table);
    if (!resolved.ok) return [];
    if (resolved.item.id !== selfId)
      return storedEdits(reference, qualifier, resolved.item);
    return isNamedReference(reference) ? [] : [{ span: qualifier, text: "" }];
  });
}

/** A stored reference's columns by their current letters, when every name is found. */
function letterEdits(
  reference: FreeformReference,
  columns: readonly string[] | undefined,
): Edit[] | null {
  if (!isNamedReference(reference)) return [];
  const places = reference.columns.map(({ column }) =>
    column.kind === "name" && columns ? columns.indexOf(column.name) + 1 : 0,
  );
  if (places.includes(0)) return null;
  return reference.columns.map(({ span }, index) => ({
    span,
    text: columnName(places[index] ?? 0),
  }));
}

/**
 * What a freeform cell shows while edited: each stored table by its current
 * name, and each column stored by name by its current letter, as Excel shows
 * another sheet's cell (`'Prices'!C2`). A column its table no longer has, or
 * one of a table whose columns aren't known, shows by name (`'Prices'[Case
 * Price]2`), which stores back as it was.
 */
export function freeformFormulaText(
  stored: string,
  tables: readonly WorkbookTableItem[],
): string {
  return rewriteReferences(stored, (reference) => {
    const { table, qualifier } = reference;
    if (!qualifier || table.kind !== "id") return [];
    const resolved = resolveTable(tables, table);
    if (!resolved.ok) return [];
    const name = freeformTableName(resolved.item.name);
    const letters = letterEdits(reference, resolved.item.columns);
    if (!letters) return [{ span: qualifier, text: name }];
    return [{ span: qualifier, text: `${name}!` }, ...letters];
  });
}

/**
 * The columns of each other table a freeform sheet's Formulas read, by
 * table id, for reading only those columns: each column named by a
 * reference, plus every column between a range's two ends in the table's
 * current order (`tables[].columns`). `"all"` for a table any reference
 * reads by position (a reference stored before names, or one typed while
 * the table's columns were unknown), or a named range across columns of a
 * table whose columns aren't known. A name the table no longer has reads
 * nothing (the Plan reports it). Names come in the table's column order.
 */
export function freeformTableColumns(
  sheet: Pick<WorkbookFreeformSheet, "id" | "cells">,
  tables: readonly WorkbookTableItem[],
): Map<string, readonly string[] | "all"> {
  const read = new Map<string, readonly string[] | "all">();
  for (const input of Object.values(sheet.cells))
    for (const reference of freeformReferences(input)) {
      if (reference.table.kind === "local") continue;
      const resolved = resolveTable(tables, reference.table);
      if (!resolved.ok || resolved.item.id === sheet.id) continue;
      const { id, columns } = resolved.item;
      const names = referenceColumnNames(reference, columns);
      read.set(id, joinColumns(read.get(id) ?? [], names, columns));
    }
  return read;
}

/** Columns read so far joined with more, in the table's order when it is known, else as first read. */
function joinColumns(
  known: readonly string[] | "all",
  names: ReadonlySet<string> | "all",
  columns: readonly string[] | undefined,
): readonly string[] | "all" {
  if (known === "all" || names === "all") return "all";
  const union = new Set([...known, ...names]);
  return columns ? columns.filter((name) => union.has(name)) : [...union];
}

/** The column names one reference reads of its table, or `"all"`. */
function referenceColumnNames(
  reference: FreeformReference,
  columns: readonly string[] | undefined,
): ReadonlySet<string> | "all" {
  if (!isNamedReference(reference)) return "all";
  const names = new Set(
    reference.columns.flatMap(({ column }) =>
      column.kind === "name" ? [column.name] : [],
    ),
  );
  if (names.size === 1 && !columns) return names;
  const resolved = freeformReferenceRange(reference, columns);
  if (resolved.ok) {
    const { start, end } = resolved.range;
    return new Set(columns?.slice(start.column - 1, end.column));
  }
  return resolved.missing === null ? "all" : new Set();
}
